import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Bed } from 'lucide-react';
import { NumberStepper } from '../../ui/number-stepper';

/**
 * Shared RoomConfiguration component for Quotation Wizard
 * Updated for mobile-responsive spec (ontof-quotation-v2-3.html)
 */
const RoomConfiguration = ({
  roomConfig,
  onRoomConfigChange,
  showExtendedBeds = true,
  compact = false,
}) => {
  const handleChange = (field, value) => {
    onRoomConfigChange({
      ...roomConfig,
      [field]: value,
      auto_calculate: false,  // Disable auto when manually changing
    });
  };

  const handleAutoToggle = (checked) => {
    onRoomConfigChange({
      ...roomConfig,
      auto_calculate: checked,
    });
  };

  // Calculate totals for summary
  const totalRooms = (roomConfig.single_rooms || 0) + (roomConfig.double_rooms || 0) + (roomConfig.triple_rooms || 0);
  const totalExtraBeds = (roomConfig.adult_extra_beds || 0) + (roomConfig.children_with_bed || 0);

  const renderRoomSummary = () => {
    if (roomConfig.auto_calculate) {
      return (
        <span>
          <strong className="text-[var(--navy)]">Auto-calculated:</strong>{' '}
          {roomConfig.double_rooms > 0 ? `${roomConfig.double_rooms} Double Room${roomConfig.double_rooms > 1 ? 's' : ''}` : ''}{' '}
          {roomConfig.single_rooms > 0 ? `${roomConfig.single_rooms} Single Room${roomConfig.single_rooms > 1 ? 's' : ''}` : ''}{' '}
          {roomConfig.triple_rooms > 0 ? `${roomConfig.triple_rooms} Triple Room${roomConfig.triple_rooms > 1 ? 's' : ''}` : ''}{' '}
          {showExtendedBeds && roomConfig.children_with_bed > 0 ? `| ${roomConfig.children_with_bed} Child Bed${roomConfig.children_with_bed > 1 ? 's' : ''}` : ''}{' '}
          {!showExtendedBeds && (roomConfig.extra_beds || totalExtraBeds) > 0 ? `| ${roomConfig.extra_beds || totalExtraBeds} Extra Bed${(roomConfig.extra_beds || totalExtraBeds) > 1 ? 's' : ''}` : ''}{' '}
          {showExtendedBeds && roomConfig.infants > 0 ? `| ${roomConfig.infants} Infant${roomConfig.infants > 1 ? 's' : ''}` : ''}
        </span>
      );
    }
    return (
      <span>
        <strong className="text-[var(--navy)]">Manual:</strong> Configure rooms and beds based on your requirements.
        Total rooms: {totalRooms}
      </span>
    );
  };

  if (compact) {
    // Compact layout for CustomPackageBuilder
    const totalPaxNeedingBeds = (roomConfig.single_rooms || 0) + 
      ((roomConfig.double_rooms || 0) * 2) + 
      ((roomConfig.triple_rooms || 0) * 3) + 
      (roomConfig.extra_beds || 0);
    
    return (
      <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]" data-testid="room-config-card">
        <CardHeader className="py-3 px-4 sm:px-6 bg-white border-b border-[var(--g200)]">
          <CardTitle className="text-sm font-bold flex items-center justify-between">
            <span className="flex items-center gap-2 text-[var(--navy)]">
              <Bed className="w-4 h-4 text-[var(--navy)]" />
              Room Configuration
            </span>
            <div className="flex items-center gap-2">
              <Switch
                id="auto-rooms-compact"
                checked={roomConfig.auto_calculate}
                onCheckedChange={handleAutoToggle}
              />
              <Label htmlFor="auto-rooms-compact" className="text-xs font-medium cursor-pointer text-[var(--g600)]">Auto</Label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Mobile: 2-col, Desktop: flex wrap */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-end gap-4 sm:gap-6 justify-center sm:justify-start">
            <NumberStepper
              label="Single"
              value={roomConfig.single_rooms || 0}
              onChange={(val) => handleChange('single_rooms', val)}
              min={0}
              max={10}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
            <NumberStepper
              label="Double"
              value={roomConfig.double_rooms || 1}
              onChange={(val) => handleChange('double_rooms', val)}
              min={0}
              max={10}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
            <NumberStepper
              label="Triple"
              value={roomConfig.triple_rooms || 0}
              onChange={(val) => handleChange('triple_rooms', val)}
              min={0}
              max={10}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
            <NumberStepper
              label="Extra Beds"
              value={roomConfig.extra_beds || 0}
              onChange={(val) => handleChange('extra_beds', val)}
              min={0}
              max={10}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
          </div>
          
          {/* Explore Alternative Rooms Toggle - Compact */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 p-3 bg-[var(--blue-light)] rounded-lg border border-[rgba(37,99,235,0.15)]">
            <Switch
              id="explore-alternatives-compact"
              checked={roomConfig.explore_alternative_rooms || false}
              onCheckedChange={(checked) => handleChange('explore_alternative_rooms', checked)}
              className="flex-shrink-0"
            />
            <Label htmlFor="explore-alternatives-compact" className="text-xs text-[var(--navy)] cursor-pointer">
              Explore alternative rooms (villas, suites) for {totalPaxNeedingBeds} pax
            </Label>
          </div>
          
          <p className="text-xs text-[var(--g500)] text-center sm:text-left">
            {roomConfig.auto_calculate ? (
              <span className="text-[var(--navy)]">
                Auto: {roomConfig.double_rooms > 0 ? `${roomConfig.double_rooms} Double` : ''}{roomConfig.single_rooms > 0 ? ` + ${roomConfig.single_rooms} Single` : ''}{(roomConfig.extra_beds || 0) > 0 ? ` + ${roomConfig.extra_beds} Extra Bed${roomConfig.extra_beds > 1 ? 's' : ''}` : ''}
              </span>
            ) : (
              'Manually configure rooms and extra beds as needed.'
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Full layout for Predefined/Custom routes
  return (
    <Card className="border-[var(--g200)] shadow-[var(--sh-xs)]" data-testid="room-config-card">
      <CardHeader className="py-3 px-4 sm:px-6 bg-white border-b border-[var(--g200)]">
        <CardTitle className="text-sm font-bold flex items-center justify-between">
          <span className="flex items-center gap-2 text-[var(--navy)]">
            <Bed className="w-4 h-4 text-[var(--navy)]" />
            Room Configuration
          </span>
          <div className="flex items-center gap-2">
            <Switch
              id="auto-rooms-main"
              checked={roomConfig.auto_calculate}
              onCheckedChange={handleAutoToggle}
            />
            <Label htmlFor="auto-rooms-main" className="text-xs font-medium cursor-pointer text-[var(--g600)]">Auto-calculate</Label>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 space-y-4">
        {/* Room Types - Mobile: 3-col grid, Desktop: flex wrap */}
        <div>
          <h4 className="text-xs font-semibold text-[var(--g800)] uppercase mb-3">Room Types</h4>
          <div className="grid grid-cols-3 sm:flex sm:flex-wrap items-end gap-3 sm:gap-4 justify-center sm:justify-start">
            <NumberStepper
              label="Single"
              value={roomConfig.single_rooms || 0}
              onChange={(val) => handleChange('single_rooms', val)}
              min={0}
              max={20}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
            <NumberStepper
              label="Double"
              value={roomConfig.double_rooms || 1}
              onChange={(val) => handleChange('double_rooms', val)}
              min={0}
              max={20}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
            <NumberStepper
              label="Triple"
              value={roomConfig.triple_rooms || 0}
              onChange={(val) => handleChange('triple_rooms', val)}
              min={0}
              max={20}
              disabled={roomConfig.auto_calculate}
              size="sm"
            />
          </div>
        </div>

        {/* Bed Configuration - Only for extended view */}
        {showExtendedBeds && (
          <div className="border-t border-[var(--g200)] pt-4">
            <h4 className="text-xs font-semibold text-[var(--g800)] uppercase mb-3">Bed Configuration</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 items-end gap-3 sm:gap-4 justify-center sm:justify-start">
              <NumberStepper
                label="Adult Extra"
                value={roomConfig.adult_extra_beds || 0}
                onChange={(val) => handleChange('adult_extra_beds', val)}
                min={0}
                max={20}
                disabled={roomConfig.auto_calculate}
                size="sm"
              />
              <NumberStepper
                label="Child Bed"
                value={roomConfig.children_with_bed || 0}
                onChange={(val) => handleChange('children_with_bed', val)}
                min={0}
                max={20}
                disabled={roomConfig.auto_calculate}
                size="sm"
              />
              <NumberStepper
                label="Child No Bed"
                value={roomConfig.children_without_bed || 0}
                onChange={(val) => handleChange('children_without_bed', val)}
                min={0}
                max={20}
                disabled={roomConfig.auto_calculate}
                size="sm"
              />
              <NumberStepper
                label="Infants"
                value={roomConfig.infants || 0}
                onChange={(val) => handleChange('infants', val)}
                min={0}
                max={20}
                disabled={roomConfig.auto_calculate}
                size="sm"
              />
            </div>
          </div>
        )}

        {/* Explore Alternative Rooms Toggle */}
        <div className="border-t border-[var(--g200)] pt-4">
          <div className="flex flex-col sm:flex-row items-start gap-3 p-3 bg-[var(--blue-light)] rounded-lg border border-[rgba(37,99,235,0.15)]">
            <Switch
              id="explore-alternatives"
              checked={roomConfig.explore_alternative_rooms || false}
              onCheckedChange={(checked) => handleChange('explore_alternative_rooms', checked)}
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <Label htmlFor="explore-alternatives" className="text-xs font-semibold text-[var(--navy)] cursor-pointer">
                Explore alternative room options
              </Label>
              <p className="text-[11px] text-[var(--g500)] mt-1">
                Show villas, suites, and larger rooms for {
                  (roomConfig.single_rooms || 0) + 
                  ((roomConfig.double_rooms || 0) * 2) + 
                  ((roomConfig.triple_rooms || 0) * 3) + 
                  (roomConfig.adult_extra_beds || 0) + 
                  (roomConfig.children_with_bed || 0)
                } pax requiring beds
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[var(--g50)] p-3 rounded-lg border border-[var(--g200)]">
          <p className="text-xs text-[var(--g700)]">
            {renderRoomSummary()}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default RoomConfiguration;
